import { useCallback } from "react";
import { IExtendedUsuario, IUsuario, IUsuarioTable } from "../../application/models/IUsuario";
import { IPerfil } from "../../application/models/IPerfil";
import { delay } from "../../utils/delay";
import { useAxiosInstances } from "../axios-instance";
import { IAcesso, ITipoUsuario } from "../../application/models";
import { useNavigationService } from "../../use-cases/navigation";

export const useUserApi = () => {
  const { axiosAuthInstance } = useAxiosInstances();
  const { navigateTo } = useNavigationService();

  const findUsuariosByIdClienteApi = useCallback(
    async (data: { idCliente: number; page: number; limit: number }): Promise<{
      list: Omit<IUsuarioTable, "Senha">[];
      total: number;
    }> => {
      try {
        const result = await axiosAuthInstance.get(`/usuario/cliente/${data.idCliente}`, {
          params: {
            Page: data.page,
            Limit: data.limit,
          },
        });

        return {
          list: result.data.list,
          total: result.data.total,
        };
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const getUserByIdApi = useCallback(
    async (data: { IdUsuario: number }): Promise<{ usuario: IExtendedUsuario }> => {
      try {
        const result = await axiosAuthInstance.get(`/usuario/${data.IdUsuario}`);
        return {
          usuario: result.data.usuario,
        };
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const getUserPerfilApi = useCallback(
    async (data: { id: string }): Promise<IPerfil> => {
      try {
        const result = await axiosAuthInstance.get(`/perfil/${data.id}`);
        return result.data;
      } catch (error) {
        const perfil: IPerfil = {
          IdPerfil: 0,
          Descricao: "",
          Ativo: true,
          Atualizacao: null,
          IdCliente: 0,
          Inclusao: new Date(),
        };
        return perfil;
      }
    },
    [axiosAuthInstance]
  );

  const getVendedoresApi = useCallback(async (): Promise<IUsuario[]> => {
    try {
      const result = await axiosAuthInstance.get("/usuario/vendedores");
      return result.data;
    } catch (error) {
      const usuarioMock: IUsuario[] = [
        {
          IdUsuario: 0,
          IdCliente: 0,
          Nome: "",
          LoginUsuario: "",
          Senha: "",
          Email: "",
          Ativo: false,
          Inclusao: new Date(),
          Atualizacao: null,
        },
      ];
      return usuarioMock;
    }
  }, [axiosAuthInstance]);

  const createUserApi = useCallback(
    async (data: {
      nome: string;
      email: string;
      login: string;
      idPerfil: number;
      idCliente: number;
      tipoUsuario?: ITipoUsuario;
    }): Promise<void> => {
      try {
        const result = await axiosAuthInstance.post(`/usuario`, {
          Nome: data.nome,
          Email: data.email,
          Login: data.login,
          IdCliente: data.idCliente,
          IdPerfil: data.idPerfil,
          TipoUsuario: data.tipoUsuario,
        });

        return result.data;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const updateUserApi = useCallback(
    async (data: {
      idUsuario: number;
      nome: string;
      email: string;
      ativo: boolean;
      idCliente: number;
      idPerfil: number;
      tipoUsuario?: ITipoUsuario;
    }): Promise<void> => {
      try {
        const result = await axiosAuthInstance.put(`/usuario/${data.idUsuario}`, {
          Nome: data.nome,
          Email: data.email,
          Ativo: data.ativo,
          IdCliente: data.idCliente,
          IdPerfil: data.idPerfil,
          TipoUsuario: data.tipoUsuario,
        });

        return result.data.usuario;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const isVelesUserApi = useCallback(
    async (data: { id: string }): Promise<boolean> => {
      try {
        const result = await axiosAuthInstance.get(`/usuario/is-veles/${data.id}`);
        return result.data.isVeles;
      } catch (error) {
        return false;
      }
    },
    [axiosAuthInstance]
  );

  const changeUserPasswordApi = useCallback(
    async (data: { id: string; password: string }): Promise<void> => {
      try {
        await axiosAuthInstance.put(`/usuario/${data.id}/password`, {
          password: data.password,
        });
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  const verifyAccessProductApi = useCallback(
    async (data: { IdUsuario: number; IdProduto: number }): Promise<IAcesso> => {
      try {
        const result = await axiosAuthInstance.get("/auth/verify-acesso", {
          params: {
            idUsuario: data.IdUsuario,
            idProduto: data.IdProduto,
          },
        });

        return result.data;
      } catch (error) {
        throw error;
      }
    },
    [axiosAuthInstance]
  );

  return {
    getUserByIdApi,
    getUserPerfilApi,
    getVendedoresApi,
    findUsuariosByIdClienteApi,
    createUserApi,
    updateUserApi,
    isVelesUserApi,
    changeUserPasswordApi,
    verifyAccessProductApi,
  };
};